import React, { useState, useMemo, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MAP from '../data/properties.jsx';
import MapModal from '../components/MapModal.jsx';
import PageNavigation from '../components/PageNavigation.jsx';

const EyeWatcher = lazy(() => import('../components/EyeWatcher.jsx'));
const InkFlow = lazy(() => import('../components/InkFlow.jsx'));
const FozzilAI = lazy(() => import('../components/FozzilAI.jsx'));
const Vault = lazy(() => import('../components/Vault.jsx'));
const Timeline = lazy(() => import('../components/Timeline.jsx'));

function DetailPane({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="modal" onClick={onClose} role="dialog" aria-modal="true">
      <div className="box" onClick={e => e.stopPropagation()}>
        <h2 style={{ color: '#c9a62b' }}>{item.tag}</h2>
        <p style={{ color: '#bdbdbd' }}>{item.label}</p>
        <div style={{ marginTop: 12 }}>
          <strong>Type: </strong>{item.type}
        </div>
        <div style={{ marginTop: 12 }}>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [hover, setHover] = useState(null);
  const [inkActive, setInkActive] = useState(false);
  const [triggerWink, setTriggerWink] = useState(false);

  // Convert URL slug back to category name
  const categoryName = useMemo(() => {
    const nameMap = {
      'residential': 'Residential',
      'farms-nature': 'Farms & Nature',
      'apartments': 'Apartments',
      'holdings-trusts': 'Holdings & Trusts',
      'layouts-roads': 'Layouts & Roads',
      'themed-properties': 'Themed Properties',
      'legacy-hub': 'Legacy Hub'
    };
    return nameMap[category] || 'All Properties';
  }, [category]);

  // Filter properties by category
  const properties = useMemo(() => 
    MAP.filter(p => p.type === categoryName), 
    [categoryName]
  );

  const [openMap, setOpenMap] = useState(null);

  const handleOpen = (p) => {
    setDetail(p);
    setInkActive(true);
    setTimeout(() => setInkActive(false), 2800);
  };

  // Special rendering for Legacy Hub
  if (category === 'legacy-hub') {
    return (
      <div className="container legacy-pulse">
        <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading...</div>}>
          
          {/* Page Navigation */}
          <PageNavigation backTo="/" backLabel="← Back to Dashboard" />

          <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/JK legacy hub logo.png`}
                alt="Legacy Hub"
                style={{ width: 120, height: 'auto' }}
              />
            </div>
            <p style={{ color: '#bdbdbd', fontSize: 18 }}>
              Central archive for deeds, blueprints and family history. Use the vault to encrypt documents per section.
            </p>
          </div>

          {/* Vault & LIORA Echo Chamber Cards */}
          <section style={{ marginBottom: 48 }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: 24,
              maxWidth: 1200,
              margin: '0 auto',
              padding: '0 20px'
            }}>
              {/* Family Vault Card */}
              <div 
                className="feature-card"
                onClick={() => navigate('/vault')}
                style={{
                  cursor: 'pointer',
                  background: 'linear-gradient(145deg, rgba(201,166,43,0.08), rgba(201,166,43,0.02))',
                  borderColor: 'rgba(201,166,43,0.25)',
                  position: 'relative'
                }}
              >
                <div style={{ position: 'absolute', top: 20, right: 20 }}>
                  <img 
                    src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/LIORA LOGO.png`}
                    alt="Vault Logo"
                    style={{ width: 60, height: 'auto', filter: 'drop-shadow(0 4px 12px rgba(201,166,43,0.4))' }}
                  />
                </div>
                <div className="feature-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#c9a62b" strokeWidth="1.5">
                    <rect x="3" y="11" width="18" height="11" rx="2" fill="rgba(201,166,43,0.2)" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="16" r="1" fill="#c9a62b"/>
                  </svg>
                </div>
                <h3 className="feature-title">Family Vault</h3>
                <p className="feature-description">
                  Access your most precious memories and documents with enhanced security. 
                  Gallery • Diary • Stories • Family Tree • Milestones & More
                </p>
                <div style={{ marginBottom: 16 }}>
                  {/* Spacer to match Echo Chamber card height */}
                  <div style={{ minHeight: 60 }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginTop: 16 }}>
                  <div style={{ 
                    display: 'flex', 
                    gap: 8, 
                    flex: 1,
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      background: 'rgba(201,166,43,0.2)',
                      color: '#fde68a',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: 11,
                      border: '1px solid rgba(201,166,43,0.4)',
                      whiteSpace: 'nowrap'
                    }}>
                      <strong>Face Recognition</strong> Required
                    </span>
                    <span style={{
                      background: 'rgba(201,166,43,0.2)',
                      color: '#fde68a',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: 11,
                      border: '1px solid rgba(201,166,43,0.4)',
                      whiteSpace: 'nowrap'
                    }}>
                      <strong>TOTP</strong> Verification
                    </span>
                  </div>
                  <button 
                    className="btn rounded-full"
                    style={{ 
                      borderRadius: '9999px',
                      background: 'linear-gradient(135deg, #d4af37 0%, #c9a02b 100%)',
                      border: 'none',
                      padding: '10px 24px',
                      fontSize: 13,
                      fontWeight: 'bold',
                      color: '#000',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/vault');
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 16v2" strokeLinecap="round"/>
                    </svg>
                    Unlock Vault
                  </button>
                </div>
              </div>

              {/* LIORA Echo Chamber Card */}
              <div 
                className="feature-card"
                onClick={() => navigate('/echo-chamber')}
                style={{
                  cursor: 'pointer',
                  background: 'linear-gradient(145deg, rgba(147,51,234,0.12), rgba(147,51,234,0.04))',
                  borderColor: 'rgba(147,51,234,0.3)',
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                {/* Animated glow effect */}
                <div style={{
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  right: -2,
                  bottom: -2,
                  background: 'linear-gradient(135deg, rgba(147,51,234,0.3), rgba(99,102,241,0.2))',
                  borderRadius: 'inherit',
                  zIndex: -1,
                  filter: 'blur(8px)',
                  opacity: 0.5
                }} />
                
                <div style={{ position: 'absolute', top: 20, right: 20 }}>
                  <img 
                    src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/LIORA LOGO.png`}
                    alt="LIORA Logo"
                    style={{ width: 60, height: 'auto', filter: 'drop-shadow(0 4px 12px rgba(147,51,234,0.4))' }}
                  />
                </div>
                
                <div className="feature-icon" style={{
                  textShadow: '0 0 20px rgba(147,51,234,0.8)'
                }}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="url(#echoGradient)" strokeWidth="1.5">
                    <defs>
                      <linearGradient id="echoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#9333ea" />
                        <stop offset="100%" stopColor="#c084fc" />
                      </linearGradient>
                    </defs>
                    <circle cx="12" cy="12" r="10" fill="rgba(147,51,234,0.2)" />
                    <circle cx="12" cy="12" r="6" fill="rgba(147,51,234,0.3)" />
                    <circle cx="12" cy="12" r="2" fill="#9333ea" />
                    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeLinecap="round" />
                    <path d="M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" />
                  </svg>
                </div>
                <h3 className="feature-title" style={{ 
                  background: 'linear-gradient(135deg, #9333ea, #c084fc)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  LIORA Echo Chamber
                </h3>
                <p className="feature-description">
                  AI-powered sanctuary where past, present, and future converse. 
                  Voice-activated emotional conversations across generations.
                </p>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    fontSize: 13,
                    color: '#fbbf24',
                    marginBottom: 12
                  }}>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <strong>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}>
                          <circle cx="12" cy="8" r="4" />
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Past Voices
                      </strong>
                    </span>
                    <span>•</span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <strong>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}>
                          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="12" y1="19" x2="12" y2="22" strokeLinecap="round"/>
                        </svg>
                        Present
                      </strong>
                    </span>
                    <span>•</span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <strong>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}>
                          <circle cx="12" cy="8" r="4" />
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Future
                      </strong>
                    </span>
                  </div>
                  
                  {/* Example Interaction Preview */}
                  <div style={{
                    background: 'rgba(147,51,234,0.1)',
                    borderRadius: 12,
                    padding: 12,
                    marginTop: 12,
                    border: '1px solid rgba(147,51,234,0.2)'
                  }}>
                    <p style={{ 
                      color: '#c4b5fd', 
                      fontSize: 12, 
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                      margin: 0
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}>
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      "I miss Mom's voice"<br/>
                      <span style={{ color: '#9ca3af', fontSize: 11 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}>
                          <path d="M9 18V5l12-2v13" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="6" cy="18" r="3" />
                          <circle cx="18" cy="16" r="3" />
                        </svg>
                        LIORA plays laughter → "I'm still here in every hug"
                      </span>
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginTop: 16 }}>
                  <div style={{ 
                    display: 'flex', 
                    gap: 8, 
                    flex: 1,
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      background: 'rgba(147,51,234,0.2)',
                      color: '#c4b5fd',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: 11,
                      border: '1px solid rgba(147,51,234,0.4)',
                      whiteSpace: 'nowrap',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Voice
                    </span>
                    <span style={{
                      background: 'rgba(99,102,241,0.2)',
                      color: '#c7d2fe',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: 11,
                      border: '1px solid rgba(99,102,241,0.4)',
                      whiteSpace: 'nowrap',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="6" width="20" height="12" rx="2" />
                        <path d="M7 6V4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
                        <circle cx="12" cy="12" r="2" fill="currentColor"/>
                      </svg>
                      AI
                    </span>
                    <span style={{
                      background: 'rgba(168,85,247,0.2)',
                      color: '#e9d5ff',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: 11,
                      border: '1px solid rgba(168,85,247,0.4)',
                      whiteSpace: 'nowrap',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="rgba(168,85,247,0.3)"/>
                      </svg>
                      Emotional
                    </span>
                  </div>
                  <button 
                    className="btn rounded-full"
                    style={{ 
                      borderRadius: '9999px',
                      background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
                      border: 'none',
                      padding: '10px 24px',
                      fontSize: 13,
                      fontWeight: 'bold',
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 4px 12px rgba(147,51,234,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/echo-chamber');
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <circle cx="12" cy="12" r="6" />
                      <circle cx="12" cy="12" r="2" fill="currentColor"/>
                    </svg>
                    Enter
                  </button>
                </div>
              </div>
            </div>
          </section>

          <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
          <Suspense fallback={null}><FozzilAI /></Suspense>
        </Suspense>
      </div>
    );
  }

  // Special rendering for LIORA House
  if (category === 'liora-house') {
    return (
      <div className="container legacy-pulse">
        <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading...</div>}>
          
          {/* Page Navigation */}
          <PageNavigation backTo="/" backLabel="← Back to Dashboard" />

          <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/LIORA LOGO.png`}
                alt="LIORA House"
                style={{ width: 120, height: 'auto' }}
              />
            </div>
            <p style={{ color: '#bdbdbd', fontSize: 18, maxWidth: 800, margin: '0 auto' }}>
              The crown jewel of the JK Legacy. Where architectural excellence meets timeless heritage.
            </p>
          </div>

          {/* LIORA Content Cards */}
          <section style={{ marginBottom: 48, maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: 32,
              marginTop: 48
            }}>
              {/* LIORA House Card */}
              <div style={{
                background: 'linear-gradient(145deg, rgba(201,166,43,0.12), rgba(201,166,43,0.04))',
                border: '2px solid rgba(201,166,43,0.3)',
                borderRadius: 24,
                padding: 40,
                boxShadow: '0 10px 40px rgba(201,166,43,0.2)'
              }}>
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/LIORA/liora house.jpg`}
                  alt="LIORA House"
                  style={{ 
                    width: '100%', 
                    height: 240,
                    objectFit: 'cover',
                    borderRadius: 16,
                    marginBottom: 24,
                    border: '2px solid rgba(201,166,43,0.3)'
                  }}
                />
                <h3 style={{ 
                  color: '#c9a62b', 
                  fontSize: 32, 
                  marginBottom: 20,
                  fontFamily: 'Arsenica',
                  textAlign: 'center'
                }}>
                  LIORA House
                </h3>
                <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                  Built in 2026, LIORA House stands as a monument to architectural vision and family values. 
                  This heritage mansion has witnessed generations of joy, celebration, and cherished memories. 
                  Every corner tells a story, every room holds a legacy.
                </p>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 12,
                  justifyContent: 'center'
                }}>
                  <span style={{
                    background: 'rgba(201,166,43,0.2)',
                    color: '#fde68a',
                    padding: '6px 16px',
                    borderRadius: '9999px',
                    fontSize: 13,
                    border: '1px solid rgba(201,166,43,0.4)'
                  }}>
                    <strong>Est.</strong> 2026
                  </span>
                  <span style={{
                    background: 'rgba(201,166,43,0.2)',
                    color: '#fde68a',
                    padding: '6px 16px',
                    borderRadius: '9999px',
                    fontSize: 13,
                    border: '1px solid rgba(201,166,43,0.4)'
                  }}>
                    <strong>5</strong> Bedrooms
                  </span>
                  <span style={{
                    background: 'rgba(201,166,43,0.2)',
                    color: '#fde68a',
                    padding: '6px 16px',
                    borderRadius: '9999px',
                    fontSize: 13,
                    border: '1px solid rgba(201,166,43,0.4)'
                  }}>
                    Heritage Mansion
                  </span>
                </div>
              </div>

              {/* LIORA Palace Card */}
              <div style={{
                background: 'linear-gradient(145deg, rgba(201,166,43,0.12), rgba(201,166,43,0.04))',
                border: '2px solid rgba(201,166,43,0.3)',
                borderRadius: 24,
                padding: 40,
                boxShadow: '0 10px 40px rgba(201,166,43,0.2)'
              }}>
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/LIORA/liora palace.jpeg`}
                  alt="LIORA Palace"
                  style={{ 
                    width: '100%', 
                    height: 240,
                    objectFit: 'cover',
                    borderRadius: 16,
                    marginBottom: 24,
                    border: '2px solid rgba(201,166,43,0.3)'
                  }}
                />
                <h3 style={{ 
                  color: '#c9a62b', 
                  fontSize: 32, 
                  marginBottom: 20,
                  fontFamily: 'Arsenica',
                  textAlign: 'center'
                }}>
                  LIORA Palace
                </h3>
                <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                  The majestic LIORA Palace embodies the pinnacle of luxury and tradition. 
                  With grand halls, ornate gardens, and timeless elegance, it serves as the 
                  ceremonial heart of the family legacy. A place where history and future converge.
                </p>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 12,
                  justifyContent: 'center'
                }}>
                  <span style={{
                    background: 'rgba(201,166,43,0.2)',
                    color: '#fde68a',
                    padding: '6px 16px',
                    borderRadius: '9999px',
                    fontSize: 13,
                    border: '1px solid rgba(201,166,43,0.4)'
                  }}>
                    Grand Estate
                  </span>
                  <span style={{
                    background: 'rgba(201,166,43,0.2)',
                    color: '#fde68a',
                    padding: '6px 16px',
                    borderRadius: '9999px',
                    fontSize: 13,
                    border: '1px solid rgba(201,166,43,0.4)'
                  }}>
                    <strong>10</strong> Suites
                  </span>
                  <span style={{
                    background: 'rgba(201,166,43,0.2)',
                    color: '#fde68a',
                    padding: '6px 16px',
                    borderRadius: '9999px',
                    fontSize: 13,
                    border: '1px solid rgba(201,166,43,0.4)'
                  }}>
                    Royal Legacy
                  </span>
                </div>
              </div>
            </div>

            {/* Legacy Features */}
            <div style={{ 
              marginTop: 60,
              background: 'linear-gradient(135deg, rgba(201,166,43,0.08) 0%, rgba(201,166,43,0.02) 100%)',
              border: '1px solid rgba(201,166,43,0.2)',
              borderRadius: 20,
              padding: 48,
              textAlign: 'center'
            }}>
              <h3 style={{ 
                color: '#c9a62b', 
                fontSize: 28, 
                marginBottom: 32,
                fontFamily: 'Arsenica'
              }}>
                LIORA Heritage Features
              </h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 32,
                maxWidth: 1000,
                margin: '0 auto'
              }}>
                <div>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L9 8H3L8 12L6 18L12 14L18 18L16 12L21 8H15L12 2Z" fill="#c9a62b" opacity="0.3"/>
                      <path d="M12 2V14M9 8H3L8 12M15 8H21L16 12M6 18L12 14M18 18L12 14" stroke="#c9a62b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h4 style={{ color: '#c9a62b', fontSize: 18, marginBottom: 8 }}>Ancient Gardens</h4>
                  <p style={{ color: '#9ca3af', fontSize: 14, lineHeight: 1.6 }}>
                    Century-old trees and meticulously maintained landscapes
                  </p>
                </div>
                <div>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="#c9a62b" strokeWidth="1.5" fill="none"/>
                      <path d="M3 9h18M9 21V9M15 21V9" stroke="#c9a62b" strokeWidth="1.5" opacity="0.5"/>
                      <circle cx="6" cy="6" r="1" fill="#c9a62b"/>
                    </svg>
                  </div>
                  <h4 style={{ color: '#c9a62b', fontSize: 18, marginBottom: 8 }}>Art Collection</h4>
                  <p style={{ color: '#9ca3af', fontSize: 14, lineHeight: 1.6 }}>
                    Curated family heirlooms and timeless artifacts
                  </p>
                </div>
                <div>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="#c9a62b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="#c9a62b" strokeWidth="1.5" fill="rgba(201,166,43,0.1)"/>
                      <path d="M8 7h8M8 11h5" stroke="#c9a62b" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
                    </svg>
                  </div>
                  <h4 style={{ color: '#c9a62b', fontSize: 18, marginBottom: 8 }}>Heritage Library</h4>
                  <p style={{ color: '#9ca3af', fontSize: 14, lineHeight: 1.6 }}>
                    Ancient texts and family chronicles spanning generations
                  </p>
                </div>
                <div>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15 8.5L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L9 8.5L12 2Z" stroke="#c9a62b" strokeWidth="1.5" fill="rgba(201,166,43,0.2)" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h4 style={{ color: '#c9a62b', fontSize: 18, marginBottom: 8 }}>Grand Ballroom</h4>
                  <p style={{ color: '#9ca3af', fontSize: 14, lineHeight: 1.6 }}>
                    Host to countless celebrations and family gatherings
                  </p>
                </div>
              </div>
            </div>
          </section>

          <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
          <Suspense fallback={null}><FozzilAI /></Suspense>
        </Suspense>
      </div>
    );
  }

  // Special rendering for Residential
  if (category === 'residential') {
    return (
      <div className="container legacy-pulse">
        <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading...</div>}>
          <PageNavigation backTo="/" backLabel="← Back to Dashboard" />

          <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/JK Residency logo.png`}
                alt="Residential"
                style={{ width: 120, height: 'auto' }}
              />
            </div>
            <p style={{ color: '#bdbdbd', fontSize: 18, maxWidth: 800, margin: '0 auto' }}>
              Legacy residences where generations have built memories and called home.
            </p>
          </div>

          <section style={{ marginBottom: 48, maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32, marginTop: 48 }}>
              {properties.map(p => (
                <div key={p.id} style={{
                  background: 'linear-gradient(145deg, rgba(201,166,43,0.12), rgba(201,166,43,0.04))',
                  border: '2px solid rgba(201,166,43,0.3)',
                  borderRadius: 24,
                  padding: 40,
                  boxShadow: '0 10px 40px rgba(201,166,43,0.2)',
                  cursor: 'pointer'
                }} onClick={() => handleOpen(p)}>
                  <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#c9a62b" strokeWidth="1.5" fill="rgba(201,166,43,0.15)"/>
                      <path d="M9 22V12h6v10" stroke="#c9a62b" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <h3 style={{ color: '#c9a62b', fontSize: 28, marginBottom: 16, fontFamily: 'Arsenica', textAlign: 'center' }}>
                    {p.tag}
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 24, textAlign: 'center' }}>
                    {p.label}
                  </p>
                  {p.mapLink && (
                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                      <button
                        className="btn"
                        onClick={(e)=>{ e.stopPropagation(); setOpenMap({url:p.mapLink, title:p.tag}); }}
                        style={{ background: 'rgba(201,166,43,0.2)', border: '1px solid rgba(201,166,43,0.4)', color: '#fde68a', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" fill="rgba(201,166,43,0.3)"/>
                        </svg>
                        View on Map
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
          <Suspense fallback={null}><FozzilAI /></Suspense>
        </Suspense>
      </div>
    );
  }

  // Special rendering for Farms & Nature
  if (category === 'farms-nature') {
    return (
      <div className="container legacy-pulse">
        <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading...</div>}>
          <PageNavigation backTo="/" backLabel="← Back to Dashboard" />

          <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/JK Farmscapes logo.png`}
                alt="Farms & Nature"
                style={{ width: 120, height: 'auto' }}
              />
            </div>
            <p style={{ color: '#bdbdbd', fontSize: 18, maxWidth: 800, margin: '0 auto' }}>
              Organic farmlands and natural sanctuaries cultivating heritage and harvest.
            </p>
          </div>

          <section style={{ marginBottom: 48, maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32, marginTop: 48 }}>
              {properties.map(p => (
                <div key={p.id} style={{
                  background: 'linear-gradient(145deg, rgba(34,197,94,0.12), rgba(34,197,94,0.04))',
                  border: '2px solid rgba(34,197,94,0.3)',
                  borderRadius: 24,
                  padding: 40,
                  boxShadow: '0 10px 40px rgba(34,197,94,0.2)',
                  cursor: 'pointer'
                }} onClick={() => handleOpen(p)}>
                  <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#22c55e" strokeWidth="1.5" fill="rgba(34,197,94,0.15)"/>
                      <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="#22c55e" strokeWidth="1.5"/>
                      <circle cx="8" cy="9" r="1" fill="#22c55e"/>
                      <circle cx="16" cy="9" r="1" fill="#22c55e"/>
                    </svg>
                  </div>
                  <h3 style={{ color: '#22c55e', fontSize: 28, marginBottom: 16, fontFamily: 'Arsenica', textAlign: 'center' }}>
                    {p.tag}
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 24, textAlign: 'center' }}>
                    {p.label}
                  </p>
                  {p.mapLink && (
                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                      <button
                        className="btn"
                        onClick={(e)=>{ e.stopPropagation(); setOpenMap({url:p.mapLink, title:p.tag}); }}
                        style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', color: '#86efac' }}
                      >
                        📍 View on Map
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
          <Suspense fallback={null}><FozzilAI /></Suspense>
        </Suspense>
      </div>
    );
  }

  // Special rendering for Apartments
  if (category === 'apartments') {
    return (
      <div className="container legacy-pulse">
        <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading...</div>}>
          <PageNavigation backTo="/" backLabel="← Back to Dashboard" />

          <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/JK Avenue logo.png`}
                alt="Apartments"
                style={{ width: 120, height: 'auto' }}
              />
            </div>
            <p style={{ color: '#bdbdbd', fontSize: 18, maxWidth: 800, margin: '0 auto' }}>
              Modern living spaces and premium apartments for contemporary family lifestyle.
            </p>
          </div>

          <section style={{ marginBottom: 48, maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32, marginTop: 48 }}>
              {properties.map(p => (
                <div key={p.id} style={{
                  background: 'linear-gradient(145deg, rgba(59,130,246,0.12), rgba(59,130,246,0.04))',
                  border: '2px solid rgba(59,130,246,0.3)',
                  borderRadius: 24,
                  padding: 40,
                  boxShadow: '0 10px 40px rgba(59,130,246,0.2)',
                  cursor: 'pointer'
                }} onClick={() => handleOpen(p)}>
                  <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="4" y="2" width="16" height="20" rx="1" stroke="#3b82f6" strokeWidth="1.5" fill="rgba(59,130,246,0.15)"/>
                      <path d="M4 6h16M4 10h16M4 14h16M4 18h16" stroke="#3b82f6" strokeWidth="1.5" opacity="0.3"/>
                      <rect x="8" y="16" width="3" height="3" fill="#3b82f6" opacity="0.5"/>
                      <rect x="13" y="16" width="3" height="3" fill="#3b82f6" opacity="0.5"/>
                    </svg>
                  </div>
                  <h3 style={{ color: '#3b82f6', fontSize: 28, marginBottom: 16, fontFamily: 'Arsenica', textAlign: 'center' }}>
                    {p.tag}
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 24, textAlign: 'center' }}>
                    {p.label}
                  </p>
                  {p.mapLink && (
                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                      <button
                        className="btn"
                        onClick={(e)=>{ e.stopPropagation(); setOpenMap({url:p.mapLink, title:p.tag}); }}
                        style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd' }}
                      >
                        📍 View on Map
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
          <Suspense fallback={null}><FozzilAI /></Suspense>
        </Suspense>
      </div>
    );
  }

  // Special rendering for Holdings & Trusts
  if (category === 'holdings-trusts') {
    return (
      <div className="container legacy-pulse">
        <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading...</div>}>
          <PageNavigation backTo="/" backLabel="← Back to Dashboard" />

          <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/JK holdings logo.png`}
                alt="Holdings & Trusts"
                style={{ width: 120, height: 'auto' }}
              />
            </div>
            <p style={{ color: '#bdbdbd', fontSize: 18, maxWidth: 800, margin: '0 auto' }}>
              Strategic asset management and trust portfolios safeguarding family legacy.
            </p>
          </div>

          <section style={{ marginBottom: 48, maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32, marginTop: 48 }}>
              {properties.map(p => (
                <div key={p.id} style={{
                  background: 'linear-gradient(145deg, rgba(168,85,247,0.12), rgba(168,85,247,0.04))',
                  border: '2px solid rgba(168,85,247,0.3)',
                  borderRadius: 24,
                  padding: 40,
                  boxShadow: '0 10px 40px rgba(168,85,247,0.2)',
                  cursor: 'pointer'
                }} onClick={() => handleOpen(p)}>
                  <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="6" width="18" height="14" rx="2" stroke="#a855f7" strokeWidth="1.5" fill="rgba(168,85,247,0.15)"/>
                      <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="#a855f7" strokeWidth="1.5"/>
                      <path d="M3 10h18" stroke="#a855f7" strokeWidth="1.5"/>
                      <circle cx="12" cy="15" r="2" stroke="#a855f7" strokeWidth="1.5" fill="none"/>
                    </svg>
                  </div>
                  <h3 style={{ color: '#a855f7', fontSize: 28, marginBottom: 16, fontFamily: 'Arsenica', textAlign: 'center' }}>
                    {p.tag}
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 24, textAlign: 'center' }}>
                    {p.label}
                  </p>
                  {p.mapLink && (
                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                      <button
                        className="btn"
                        onClick={(e)=>{ e.stopPropagation(); setOpenMap({url:p.mapLink, title:p.tag}); }}
                        style={{ background: 'rgba(168,85,247,0.2)', border: '1px solid rgba(168,85,247,0.4)', color: '#d8b4fe' }}
                      >
                        📍 View on Map
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
          <Suspense fallback={null}><FozzilAI /></Suspense>
        </Suspense>
      </div>
    );
  }

  // Special rendering for Layouts & Roads
  if (category === 'layouts-roads') {
    return (
      <div className="container legacy-pulse">
        <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading...</div>}>
          <PageNavigation backTo="/" backLabel="← Back to Dashboard" />

          <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/JK Grove logo.png`}
                alt="Layouts & Roads"
                style={{ width: 120, height: 'auto' }}
              />
            </div>
            <p style={{ color: '#bdbdbd', fontSize: 18, maxWidth: 800, margin: '0 auto' }}>
              Planned developments and road networks connecting family properties.
            </p>
          </div>

          <section style={{ marginBottom: 48, maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32, marginTop: 48 }}>
              {properties.map(p => (
                <div key={p.id} style={{
                  background: 'linear-gradient(145deg, rgba(234,88,12,0.12), rgba(234,88,12,0.04))',
                  border: '2px solid rgba(234,88,12,0.3)',
                  borderRadius: 24,
                  padding: 40,
                  boxShadow: '0 10px 40px rgba(234,88,12,0.2)',
                  cursor: 'pointer'
                }} onClick={() => handleOpen(p)}>
                  <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 12h7l3-9 4 18 3-9h4" stroke="#ea580c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="9" stroke="#ea580c" strokeWidth="1.5" fill="rgba(234,88,12,0.1)"/>
                      <path d="M9 17l6-10" stroke="#ea580c" strokeWidth="1.5" opacity="0.3"/>
                    </svg>
                  </div>
                  <h3 style={{ color: '#ea580c', fontSize: 28, marginBottom: 16, fontFamily: 'Arsenica', textAlign: 'center' }}>
                    {p.tag}
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 24, textAlign: 'center' }}>
                    {p.label}
                  </p>
                  {p.mapLink && (
                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                      <button
                        className="btn"
                        onClick={(e)=>{ e.stopPropagation(); setOpenMap({url:p.mapLink, title:p.tag}); }}
                        style={{ background: 'rgba(234,88,12,0.2)', border: '1px solid rgba(234,88,12,0.4)', color: '#fdba74' }}
                      >
                        📍 View on Map
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
          <Suspense fallback={null}><FozzilAI /></Suspense>
        </Suspense>
      </div>
    );
  }

  // Special rendering for Themed Properties
  if (category === 'themed-properties') {
    return (
      <div className="container legacy-pulse">
        <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading...</div>}>
          <PageNavigation backTo="/" backLabel="← Back to Dashboard" />

          <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
              <img 
                src={`${process.env.PUBLIC_URL || ''}/JK Legacy Logos/JK Estates logo.png`}
                alt="Themed Properties"
                style={{ width: 120, height: 'auto' }}
              />
            </div>
            <p style={{ color: '#bdbdbd', fontSize: 18, maxWidth: 800, margin: '0 auto' }}>
              Unique heritage parks and themed sanctuaries preserving family memories.
            </p>
          </div>

          <section style={{ marginBottom: 48, maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32, marginTop: 48 }}>
              {properties.map(p => (
                <div key={p.id} style={{
                  background: 'linear-gradient(145deg, rgba(236,72,153,0.12), rgba(236,72,153,0.04))',
                  border: '2px solid rgba(236,72,153,0.3)',
                  borderRadius: 24,
                  padding: 40,
                  boxShadow: '0 10px 40px rgba(236,72,153,0.2)',
                  cursor: 'pointer'
                }} onClick={() => handleOpen(p)}>
                  <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="4" stroke="#ec4899" strokeWidth="1.5" fill="rgba(236,72,153,0.2)"/>
                      <path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l3 3M16 16l3 3M5 19l3-3M16 8l3-3" stroke="#ec4899" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3 style={{ color: '#ec4899', fontSize: 28, marginBottom: 16, fontFamily: 'Arsenica', textAlign: 'center' }}>
                    {p.tag}
                  </h3>
                  <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 24, textAlign: 'center' }}>
                    {p.label}
                  </p>
                  {p.mapLink && (
                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                      <button
                        className="btn"
                        onClick={(e)=>{ e.stopPropagation(); setOpenMap({url:p.mapLink, title:p.tag}); }}
                        style={{ background: 'rgba(236,72,153,0.2)', border: '1px solid rgba(236,72,153,0.4)', color: '#fbcfe8' }}
                      >
                        📍 View on Map
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
          <Suspense fallback={null}><FozzilAI /></Suspense>
        </Suspense>
      </div>
    );
  }

  return (
    <div className="container legacy-pulse">
      <Suspense fallback={<div style={{ textAlign: 'center', padding: 24, color: '#bdbdbd' }}>Loading...</div>}>
        
        {/* Page Navigation */}
        <PageNavigation backTo="/" backLabel="← Back to Dashboard" />

        <div style={{ textAlign: 'center', margin: '32px 0' }}>
          <h2 style={{ color: '#c9a62b', fontSize: 32, margin: '16px 0' }}>
            {categoryName}
          </h2>
          <p style={{ color: '#bdbdbd', fontSize: 16 }}>
            {properties.length} {properties.length === 1 ? 'Property' : 'Properties'}
          </p>
        </div>

        <div className="grid" role="list">
          {properties.map(p => (
            <div
              className="card"
              key={p.id}
              role="listitem"
              onClick={() => handleOpen(p)}
              onMouseEnter={() => setHover(p.id)}
              onMouseLeave={() => setHover(null)}
            >
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <h3 style={{margin:0}}>{p.tag}</h3>
                {p.mapLink && (
                  <button
                    className="map-btn"
                    onClick={(e)=>{ e.stopPropagation(); setOpenMap({url:p.mapLink, title:p.tag}); }}
                    aria-label={`Open ${p.tag} on map`}
                  >
                    <span className="map-tooltip">Open map</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="#c9a62b" strokeWidth="1" fill="none"/>
                      <circle cx="12" cy="9" r="2.2" fill="#c9a62b" />
                    </svg>
                  </button>
                )}
              </div>
              <p>{p.label}</p>
            </div>
          ))}
        </div>

        {hover && (
          <div style={{ textAlign: 'center', color: '#bdbdbd', marginTop: 8 }}>
            Hovering: {properties.find(i => i.id === hover)?.tag}
          </div>
        )}

        {properties.length === 0 && (
          <div style={{ textAlign: 'center', color: '#bdbdbd', padding: 48 }}>
            No properties found in this category.
          </div>
        )}

        {inkActive && <Suspense fallback={null}><InkFlow active={inkActive} /></Suspense>}
        <Suspense fallback={null}><EyeWatcher triggerWink={triggerWink} /></Suspense>
        <Suspense fallback={null}><FozzilAI /></Suspense>
        {detail && <DetailPane item={detail} onClose={() => setDetail(null)} />}
        {openMap && <MapModal url={openMap.url} title={openMap.title} onClose={()=>setOpenMap(null)} />}
      </Suspense>
    </div>
  );
}
